
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "reactstrap";
import { Clock, X } from "react-feather";
import { toast } from "react-toastify";
import { blockRnr, RnrHistory } from "../../../Api/apiUtils";
import { hasEmailAccess, useEmailAccessConfig } from "../../../utils/emailAccessConfig";
import { Page, PageHeader, Card, Button, Field, Select, TextInput, Badge, EmptyState } from "../../../ui";
import "./rnr.scss";


interface RnrBlockedSlot {
    id: string;
    createdOn: string; // ISO date
    date: string; // ISO date for blocked day
    fromTime: string; // HH:mm
    toTime: string; // HH:mm
    cancelledClasses: number;
}

const RnrContent: React.FC = () => {
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState<number>(currentYear);
    const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
    const [date, setDate] = useState<string>("");
    const [startTime, setStartTime] = useState<string>("");
    const [endTime, setEndTime] = useState<string>("");
    const [openModal, setOpenModal] = useState(false);
    const [pendingBlockPayload, setPendingBlockPayload] = useState<any>(null);
    const [cancelledClassesCount, setCancelledClassesCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(false); // Add loading state

    useEmailAccessConfig();
    const isCurrentUserRestricted = hasEmailAccess("rnr");

    const [blockedSlots, setBlockedSlots] = useState<any[]>([
        {
            id: "demo-1",
            createdOn: `${currentYear}-01-20`,
            date: `${currentYear}-03-20`,
            startTime: "14:00",
            endTime: "16:00",
            cancelledClasses: 8,
        },
    ]);

    const monthNames = useMemo(
        () => ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        []
    );

    const years = useMemo(() => {
        const list: number[] = [];
        for (let y = currentYear - 1; y <= currentYear + 3; y++) list.push(y);
        return list;
    }, [currentYear]);

    const timeOptions = useMemo(() => {
        const times: string[] = [];
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 30) {
                const hh = String(h).padStart(2, "0");
                const mm = String(m).padStart(2, "0");
                times.push(`${hh}:${mm}`);
            }
        }
        return times;
    }, []);

    useEffect(() => {
        getHistory();
    }, [selectedMonth, year]);
    
    const handleshowCount = async () => {
        if (!date || !startTime || !endTime) {
            toast.error("Please select date, start time and end time");
            return;
        }
    
        const selected = new Date(date);
        if (selected.getFullYear() !== year || selected.getMonth() !== selectedMonth) {
            toast.error("Please pick a date within the selected month and year");
            return;
        }
    
        if (endTime <= startTime) {
            toast.error("End time must be after start time");
            return;
        }
    
        const payload = {
            date: date,
            fromTime: `${startTime}:00`,
            toTime: `${endTime}:00`,
            status: "block",
            showCount: "true",
        };
    
        try {
            // Call the block API to get the count but do not finalize the block
            const response = await blockRnr(payload);
    
            // Assume response contains the count of cancelled classes
            setCancelledClassesCount(response.count|| 0);
            setPendingBlockPayload(payload);
            setOpenModal(true);
        } catch (e) {
            toast.error("Failed to fetch count for slot blocking");
        }
    };
    


    const handleBlock = async () => {
        if (!pendingBlockPayload) return;
    
        try {
            // Change showCount to false for final block
            const finalPayload = { ...pendingBlockPayload, showCount: "false" };
            await blockRnr(finalPayload);
            toast.success("Slot blocked successfully");
            setOpenModal(false);
            setPendingBlockPayload(null);
            setCancelledClassesCount(null);
            getHistory();
            // Also you can reset date, startTime, endTime if needed here
            setDate("");
            setStartTime("");
            setEndTime("");
        } catch (e) {
            toast.error("Failed to block slot");
        }
    };
    

    const getHistory = async () => {
        const monthPrefix = String(selectedMonth + 1).padStart(2, "0");
        const lastDay = new Date(year, selectedMonth + 1, 0).getDate();
    
        const fromDate = `${year}-${monthPrefix}-01`; 
        const toDate = `${year}-${monthPrefix}-${String(lastDay).padStart(2, "0")}`; 
    
        // const payload = { fromDate, toDate };
    
        try {
            const historyData = await RnrHistory(fromDate, toDate);


            setBlockedSlots(historyData);
            console.log("histordata", blockedSlots)
        } catch (e) {
            toast.error("Failed to load blocked slots");
        }
    };
    
  



    const handleUnblock = async (id: string) => {
        const slotToUnblock = blockedSlots.find(s => s.id === id);
        if (!slotToUnblock) return;
    
        const payload = {
            date: slotToUnblock.date.replaceAll("/", "-"), // e.g., "2025/08/21"
            fromTime: `${slotToUnblock.fromTime}`,
            toTime: `${slotToUnblock.toTime}`,
            status: "unblock",
            showCount: "false"
        };
    
        try {
            await blockRnr(payload); // Using same API for unblock with status: "unblock"
            getHistory()
            toast.success("Slot unblocked successfully");
            setBlockedSlots(prev => prev.filter(s => s.id !== id));
        } catch (e) {
            toast.error("Failed to unblock slot");
        }
    };
    

    const monthPrefix = String(selectedMonth + 1).padStart(2, "0");
    const visibleSlots = blockedSlots.filter((s) => s.date.startsWith(`${year}-${monthPrefix}`));

const hasBlockedSlots = blockedSlots.some(
  slot => slot.status === "block" && slot.date.startsWith(`${year}-${monthPrefix}`)
);


    // Reset date if changing to a different month/year where current date is out of range
    useEffect(() => {
        if (!date) return;
        const d = new Date(date);
        if (d.getFullYear() !== year || d.getMonth() !== selectedMonth) {
            setDate("");
        }
    }, [year, selectedMonth]);

    // Compute min/max for date picker constrained to selected month
    const lastDay = new Date(year, selectedMonth + 1, 0).getDate();
    const minDate = `${year}-${monthPrefix}-01`;
    const maxDate = `${year}-${monthPrefix}-${String(lastDay).padStart(2, "0")}`;

    const yearOptions = years.map((y) => ({ value: String(y), label: String(y) }));
    const startOptions = [{ value: "", label: "Select start time" }, ...timeOptions.map((t) => ({ value: t, label: t }))];
    const endOptions = [{ value: "", label: "Select end time" }, ...timeOptions.map((t) => ({ value: t, label: t }))];
    const monthLabel = `${monthNames[selectedMonth]} ${year}`;

    return (
        <Page>
            <PageHeader
                eyebrow="Teacher Operations"
                title="RnR Slot Blocking"
                subtitle="Pick a month to see its blocked slots or block a new one."
                actions={
                    <Field label="Year" className="rnr-year">
                        {(ctl) => (
                            <Select {...ctl} options={yearOptions} value={String(year)} onChange={(e) => setYear(parseInt(e.target.value))} />
                        )}
                    </Field>
                }
            />

            <Card title="Month">
                <div className="rnr-months" role="group" aria-label="Month">
                    {monthNames.map((m, idx) => (
                        <Button
                            key={m}
                            size="sm"
                            variant={idx === selectedMonth ? "primary" : "secondary"}
                            aria-pressed={idx === selectedMonth}
                            onClick={() => setSelectedMonth(idx)}
                        >
                            {m}
                        </Button>
                    ))}
                </div>
            </Card>

            <Card title={`Block a new slot · ${monthLabel}`}>
                <div className="rnr-form">
                    <Field label="Date">
                        {(ctl) => (
                            <TextInput
                                {...ctl}
                                type="date"
                                disabled={hasBlockedSlots}
                                min={minDate}
                                max={maxDate}
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                            />
                        )}
                    </Field>
                    <Field label="Start time">
                        {(ctl) => (
                            <Select {...ctl} options={startOptions} value={startTime} disabled={hasBlockedSlots} onChange={(e) => setStartTime(e.target.value)} />
                        )}
                    </Field>
                    <Field label="End time">
                        {(ctl) => (
                            <Select {...ctl} options={endOptions} value={endTime} disabled={hasBlockedSlots} onChange={(e) => setEndTime(e.target.value)} />
                        )}
                    </Field>
                    <Button
                        variant="primary"
                        icon={<Clock size={16} aria-hidden />}
                        disabled={hasBlockedSlots || !isCurrentUserRestricted}
                        onClick={handleshowCount}
                    >
                        Block slot
                    </Button>
                </div>
                {hasBlockedSlots && (
                    <p className="rnr-note" role="note">
                        This month already has a blocked slot. Unblock it first to block a different slot.
                    </p>
                )}
            </Card>

            <Card title={`Blocked slots · ${monthLabel}`}>
                {blockedSlots.length === 0 ? (
                    <EmptyState title="No slots for this month" description="Slots you block for this month will show up here." />
                ) : (
                    <ul className="rnr-slots">
                        {blockedSlots.map((slot) => {
                            const isBlocked = slot.status === "block";
                            return (
                                <li key={slot.id} className="rnr-slot">
                                    <div className="rnr-slot__main">
                                        <div className="rnr-slot__meta">
                                            <Badge tone={isBlocked ? "danger" : "success"}>{isBlocked ? "RnR blocked" : "RnR unblocked"}</Badge>
                                            <span className="ds-text-subtle">Created {new Date(slot.createdOn).toLocaleDateString()}</span>
                                        </div>
                                        <div className="ds-strong">
                                            {new Date(slot.date).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                                        </div>
                                        <div className="ds-text-muted ds-num">{slot.fromTime} – {slot.toTime}</div>
                                    </div>
                                    {isBlocked && (
                                        <Button
                                            variant="ghost"
                                            className="rnr-slot__unblock"
                                            icon={<X size={16} aria-hidden />}
                                            disabled={!isCurrentUserRestricted}
                                            onClick={() => handleUnblock(slot.id)}
                                        >
                                            Unblock
                                        </Button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Card>

            <Modal isOpen={openModal} toggle={() => setOpenModal(!openModal)} centered className="ds-dialog">
                <h2 className="ds-dialog__title">Block this slot?</h2>
                <div className="ds-dialog__body">
                    <p>Classes that will be cancelled: <strong className="ds-num">{cancelledClassesCount}</strong></p>
                </div>
                <div className="ds-dialog__actions">
                    <Button variant="secondary" onClick={() => setOpenModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleBlock} loading={loading}>Block slot</Button>
                </div>
            </Modal>
        </Page>
    );
};

export default RnrContent;